import { DateTime } from "luxon";

const baseFormat = "DD MMM YY HH:mm";

const dateUtils = {
  format: (date: DateTime, format: string) => {
    //Transform the timestamp to a local date
    const dateLocal = date.toJSDate();
    dateLocal.setMinutes(
      dateLocal.getTimezoneOffset() + dateLocal.getMinutes(),
    );

    //Format it
    return DateTime.fromJSDate(dateLocal).toFormat(format || baseFormat);
  },
  getUtcValue: (date: number) => {
    const dateObject = DateTime.fromMillis(date).toJSDate();

    return Date.UTC(
      dateObject.getFullYear(),
      dateObject.getMonth(),
      dateObject.getDate(),
    );
  },
  isDateInRangeOrEqualsByDay: (
    rangeStart: DateTime,
    rangeEnd: DateTime,
    dateToCheck: DateTime,
  ) => {
    const isDateInRange = dateToCheck >= rangeStart && dateToCheck <= rangeEnd;
    const isStartDateMatchesDateToCheck =
      rangeStart.hasSame(dateToCheck, "year") &&
      rangeStart.hasSame(dateToCheck, "month") &&
      rangeStart.hasSame(dateToCheck, "day");
    const isEndDateMatchesDateToCheck =
      rangeEnd.hasSame(dateToCheck, "year") &&
      rangeEnd.hasSame(dateToCheck, "month") &&
      rangeEnd.hasSame(dateToCheck, "day");
    return (
      isDateInRange ||
      isStartDateMatchesDateToCheck ||
      isEndDateMatchesDateToCheck
    );
  },
  formatUTC: (date: Date, format = baseFormat) =>
    DateTime.fromJSDate(date).toUTC().toFormat(format),

  dayDiff: (startDate: DateTime, endDate: DateTime) =>
    endDate.diff(startDate, ["days"]).toObject(),

  isTimestampExpired: (timestamp: number) =>
    DateTime.fromMillis(timestamp).startOf("day") <
    DateTime.now().startOf("day"),

  startOfMonth: (dateTime: DateTime) =>
    dateTime
      .startOf("month")
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
  endOfMonth: (dateTime: DateTime) =>
    dateTime
      .endOf("month")
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
  now: (dateTime: DateTime) =>
    dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
  firstOfCurrentMonth: () =>
    dateUtils.startOfMonth(DateTime.now()).toFormat("yyyy-MM-dd"),
  firstOfPreviousMonth: (month = 1) =>
    dateUtils
      .startOfMonth(DateTime.now().minus({ month }))
      .toFormat("yyyy-MM-dd"),
  returnDateFromToday: (day = 0) =>
    dateUtils.now(DateTime.now().minus({ day })).toFormat("yyyy-MM-dd"),
  daysInCurrentMonth: (): number => {
    return DateTime.now().daysInMonth ?? 0;
  },
  closestFullWeekDay: (): number => {
    const now = DateTime.now();
    if (now.day > 28) {
      return now.endOf("month").day;
    }
    if (!(now.day % 7)) {
      return now.day;
    }

    return 7 * (Math.floor(now.day / 7) + 1);
  },
  diffInDays: (date1Millis: number, date2Millis: number) => {
    return DateTime.fromMillis(date2Millis).diff(
      DateTime.fromMillis(date1Millis),
      "days",
    ).days;
  },
  formatToDateToSpecificFormat: (
    date: string,
    to: string = "MMM d, yyyy",
    from: string = "yyyy-MM-dd",
  ) => {
    return DateTime.fromFormat(date, from).toFormat(to);
  },
  thisWeekStart: () => {
    return DateTime.now().setZone("utc").startOf("week");
  },
  lastWeekStart: () => {
    return DateTime.now().minus({ week: 1 }).setZone("utc").startOf("week");
  },
};

export default dateUtils;
